import type { Filter, ViewFilter } from '@/types/api'

/**
 * Operator mapping between View format (UPPERCASE) and API format (camelCase)
 * Based on generated types from compass-be API
 */
const VIEW_TO_API_OPERATOR: Record<string, Filter['op']> = {
  EQUALS: 'eq',
  NOT_EQUALS: 'neq',
  IN: 'in',
  NOT_IN: 'notIn',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'notContains',
  GREATER_THAN: 'gt',
  GREATER_THAN_OR_EQUAL: 'gte',
  LESS_THAN: 'lt',
  LESS_THAN_OR_EQUAL: 'lte',
  IS_NULL: 'isNull',
  IS_NOT_NULL: 'isNotNull',
}

const API_TO_VIEW_OPERATOR: Record<string, ViewFilter['operator']> = {
  eq: 'EQUALS',
  neq: 'NOT_EQUALS',
  in: 'IN',
  notIn: 'NOT_IN',
  contains: 'CONTAINS',
  notContains: 'NOT_CONTAINS',
  startsWith: 'CONTAINS', // Map to closest equivalent
  endsWith: 'CONTAINS', // Map to closest equivalent
  gt: 'GREATER_THAN',
  gte: 'GREATER_THAN_OR_EQUAL',
  lt: 'LESS_THAN',
  lte: 'LESS_THAN_OR_EQUAL',
  isNull: 'IS_NULL',
  isNotNull: 'IS_NOT_NULL',
}

const VIEW_TO_API_LOGICAL_OP: Record<string, Filter['logicalOp']> = {
  AND: 'AND',
  OR: 'OR',
}

/**
 * Convert ViewFilterResponseDto (from saved views) to FilterDto (for API search)
 */
export function viewFilterToFilterDto(viewFilter: ViewFilter): Filter {
  return {
    field: viewFilter.fieldName,
    op: VIEW_TO_API_OPERATOR[viewFilter.operator] ?? 'eq',
    value: viewFilter.value ?? '',
    logicalOp:
      VIEW_TO_API_LOGICAL_OP[viewFilter.logicalOperator ?? 'AND'] ?? 'AND',
  }
}

/**
 * Convert array of ViewFilterResponseDto to FilterDto array
 */
export function viewFiltersToFilterDtos(viewFilters: ViewFilter[]): Filter[] {
  return viewFilters.map(viewFilterToFilterDto)
}

/**
 * Convert FilterDto (for API search) to partial ViewFilter format (for UI state)
 * Note: This creates a working filter without an id
 */
export function filterDtoToWorkingFilter(
  filter: Filter,
  filterOrder: number = 0
): Omit<ViewFilter, 'id'> {
  return {
    fieldName: filter.field,
    operator: API_TO_VIEW_OPERATOR[filter.op] ?? 'EQUALS',
    value: filter.value,
    logicalOperator: filter.logicalOp,
    filterOrder,
  }
}

/**
 * Serialize filters to URL search params
 */
export function serializeFiltersToUrl(filters: Filter[]): string {
  if (filters.length === 0) return ''
  return encodeURIComponent(JSON.stringify(filters))
}

/**
 * Deserialize filters from URL search params
 */
export function deserializeFiltersFromUrl(urlParam: string | null): Filter[] {
  if (!urlParam) return []
  try {
    const decoded = decodeURIComponent(urlParam)
    const parsed = JSON.parse(decoded)
    if (!Array.isArray(parsed)) return []
    return parsed as Filter[]
  } catch {
    return []
  }
}

/**
 * Check if two filter arrays are equal (for unsaved changes detection)
 */
export function areFiltersEqual(a: Filter[], b: Filter[]): boolean {
  if (a.length !== b.length) return false

  // Sort by field for comparison
  const sortedA = [...a].sort((x, y) => x.field.localeCompare(y.field))
  const sortedB = [...b].sort((x, y) => x.field.localeCompare(y.field))

  return sortedA.every((filterA, index) => {
    const filterB = sortedB[index]
    return (
      filterA.field === filterB.field &&
      filterA.op === filterB.op &&
      filterA.logicalOp === filterB.logicalOp &&
      JSON.stringify(filterA.value) === JSON.stringify(filterB.value)
    )
  })
}

/**
 * Check if two sort arrays are equal (for unsaved changes detection)
 */
export function areSortsEqual(
  a: Array<{ field: string; direction: string }>,
  b: Array<{ field: string; direction: string }>
): boolean {
  if (a.length !== b.length) return false
  return a.every((sortA, index) => {
    const sortB = b[index]
    return sortA.field === sortB.field && sortA.direction === sortB.direction
  })
}
